<template>
  <a
    :href="href"
    rel="noreferrer noopener"
    class="block group/card h-full w-full bg-white"
  >
    <div class="blog-card">
      <div class="lg:grid lg:grid-cols-6">
        <div
          v-if="!hideImage"
          v-lazy-container="{ selector: 'img' }"
          :class="[photoColSpan, reverse ? 'lg:order-last' : '']"
          class="group-hover/card:opacity-90 w-full"
        >
          <div
            v-if="post.cover_video_url"
            class="aspect-w-16 aspect-h-9"
          >
            <iframe
              :title="post.title"
              :src="post.cover_video_url"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </div>
          <vetster-post-cover-image
            v-else
            :post="post"
            :image-class="'aspect-[7/4] object-cover object-center w-full ' + (horizontal ? ' h-full' : '')"
          />
        </div>
        <div
          :class="photoColSpanInverse"
          class="p-5 flex flex-col justify-between h-full"
        >
          <div class="space-y-4 w-full">
            <div
              v-if="!hideBadges && post.post_type"
              class="space-x-1 flex flex-no-wrap justify-start items-center relative overflow-hidden before:block before:absolute before:right-0 before:top-0 before:bottom-0 before:w-4 before:bg-gradient-to-l before:from-white before:to-transparent"
            >
              <vetster-category-badge :title="post.post_type.title" />
              <vetster-category-badge
                v-for="category in post.categories"
                :key="category.id"
                :title="category.title"
              />
              <vetster-category-badge
                v-if="post?.species_types?.length > 2"
                title="All pets"
              />
              <template v-else>
                <vetster-category-badge
                  v-for="speciesType in post.species_types"
                  :key="speciesType.id"
                  :title="speciesType.name"
                />
              </template>
            </div>

            <div class="space-y-4">
              <h2
                :class="[full ? 'lg:text-3xl lg:leading-9 ' + lgLineClamp : '', lineClamp]"
                class="text-xl leading-6 transition text-ocean-900 dm-sans font-medium group-hover card:text-ocean-600"
              >
                {{ post.title }}
              </h2>
              <p
                v-if="!hideIntro && post.intro"
                class="w-full vetster-prose ignore-vetster-prose-lg line-clamp-3 text-neutral-600"
                :class="full ? 'lg:line-clamp-4' : ''"
              >
                {{ post.intro }}
              </p>
            </div>
          </div>
          <vetster-post-date
            v-if="!hideMeta && postedOn"
            :date="postedOn"
            :read-time="post.read_time"
            class="mt-8"
          />
        </div>
      </div>
    </div>
  </a>
</template>

<script>
import VetsterCategoryBadge from '@/brand-components/Content/BlogPost/VetsterCategoryBadge';
import VetsterPostCoverImage from '@/brand-components/Content/VetsterPostCoverImage';
import VetsterPostDate from '@/brand-components/Content/VetsterPostDate';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';

export default {
  name: 'VetsterPostTile',

  components: {
    VetsterCategoryBadge,
    VetsterPostCoverImage,
    VetsterPostDate,
  },

  mixins: [LocalizesRoutes],

  props: {
    full: {
      type: Boolean,
      default: false,
    },
    hideBadges: {
      type: Boolean,
      default: false,
    },
    hideImage: {
      type: Boolean,
      default: false,
    },
    hideIntro: {
      type: Boolean,
      default: false,
    },
    hideMeta: {
      type: Boolean,
      default: false,
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
    imageRatio: {
      type: Number,
      default: 6,
    },
    lines: {
      type: Number,
      default: 2,
    },
    post: {
      type: Object,
      required: true,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    href() {
      // PHP route('resources.budgeting.show')
      if (this.post.isBudgeting()) {
        return this.localizeUrl(`budgeting/${this.post.slug}`);
      }

      // PHP route('resources.glossary.show')
      if (this.post.isInGlossary()) {
        return this.localizeUrl(`glossary/${this.post.slug}`);
      }

      // PHP route('resources.type.show')
      return this.localizeLanguageUrl(`${this.post.post_type.slug}/${this.post.slug}`);
    },
    lgLineClamp() {
      return `lg:line-clamp-${this.lines + 1}`;
    },
    lineClamp() {
      return `line-clamp-${this.lines}`;
    },
    photoColSpan() {
      let ratio = this.imageRatio;
      if (ratio <= 0 || ratio >= 6) {
        ratio = 6;
      }

      return `lg:col-span-${ratio}`;
    },
    photoColSpanInverse() {
      let ratio = this.imageRatio;
      if (ratio <= 0 || ratio >= 6) {
        ratio = 0;
      }

      return `lg:col-span-${6 - ratio}`;
    },
    postedOn() {
      return this.post.posted_on ?? this.post.published_at;
    },
  },
};
</script>
